import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { QRCodeCanvas } from "qrcode.react";
import { Check, AlertTriangle, Copy, Clock } from "lucide-react";
import { useApp, API } from "../store";

export default function UsdtCheckout() {
  const { orderId } = useParams();
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("pending");
  const [now, setNow] = useState(Date.now());
  const [copied, setCopied] = useState(false);
  const { clearCart } = useApp();

  useEffect(() => {
    let stop = false;
    const poll = async () => {
      try {
        const r = await axios.get(`${API}/payments/usdt/status/${orderId}`);
        if (stop) return;
        setData(r.data);
        if (r.data.payment_status === "paid") { setStatus("paid"); clearCart(); return; }
        if (r.data.payment_status === "expired") { setStatus("expired"); return; }
        if (r.data.payment_status === "underpaid") setStatus("underpaid");
        setTimeout(poll, 8000);
      } catch {
        setTimeout(poll, 10000);
      }
    };
    poll();
    const tick = setInterval(() => setNow(Date.now()), 1000);
    return () => { stop = true; clearInterval(tick); };
  }, [orderId, clearCart]);

  const copy = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true); setTimeout(() => setCopied(false), 1500);
  };

  if (!data) return <div className="pt-40 text-center text-slate-500">Loading…</div>;

  const expiresAt = new Date(data.expires_at).getTime();
  const remaining = Math.max(0, expiresAt - now);
  const mm = String(Math.floor(remaining / 60000)).padStart(2, "0");
  const ss = String(Math.floor((remaining % 60000) / 1000)).padStart(2, "0");
  const expired = remaining <= 0;

  // TRON URI
  const uri = `tron:${data.receiving_address}?amount=${data.expected_usdt}`;

  return (
    <main className="pt-32 pb-32 bg-white min-h-screen" data-testid="usdt-checkout">
      <div className="max-w-3xl mx-auto px-6">
        <div className="overline mb-3">USDT TRC20 / TRON</div>
        <h1 className="font-serif-display text-5xl md:text-6xl text-slate-900">
          {status === "paid" ? "Payment confirmed." : status === "expired" ? "Order expired." : "Send USDT to complete order."}
        </h1>

        {status === "paid" && (
          <div className="mt-8 bg-sky-50 border border-sky-200 rounded-md p-6 flex gap-3 items-start">
            <Check size={20} className="text-sky-600 mt-1" />
            <div>
              <div className="font-semibold text-slate-900">On-chain confirmed.</div>
              <div className="text-slate-700 text-sm mt-1">Tx: <code className="bg-white px-2 py-0.5 rounded text-xs break-all">{data.tx_hash}</code></div>
            </div>
          </div>
        )}

        {data.mocked && status === "pending" && (
          <div className="mt-8 bg-amber-50 border border-amber-200 rounded-md p-4 flex gap-3 items-start">
            <AlertTriangle size={18} className="text-amber-600 mt-0.5 flex-shrink-0" />
            <div className="text-amber-900 text-sm">
              <strong>USDT MOCKED:</strong> Set <code className="bg-amber-100 px-1 rounded">USDT_TRC20_RECEIVING_ADDRESS</code> in backend/.env to activate. The flow and admin views are fully wired.
            </div>
          </div>
        )}

        {status !== "paid" && (
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-8 items-start">
            <div className="bg-white border border-slate-200 rounded-md p-6 grid place-items-center">
              <QRCodeCanvas value={uri} size={240} bgColor="#ffffff" fgColor="#0B1220" includeMargin level="M" />
              <div className="mt-4 text-xs uppercase tracking-widest text-slate-500 font-ui">Scan with your TRON wallet</div>
            </div>

            <div className="space-y-5">
              <div>
                <div className="overline mb-2">Network</div>
                <div className="text-2xl font-serif-display text-slate-900">USDT · TRC20 / TRON</div>
              </div>
              <div>
                <div className="overline mb-2">Send exactly</div>
                <div className="font-serif-display text-5xl text-sky-600">{data.expected_usdt?.toFixed(2)} <span className="text-2xl text-slate-700">USDT</span></div>
              </div>
              <div>
                <div className="overline mb-2">Receiving address</div>
                <div className="bg-slate-50 border border-slate-200 rounded-md p-3 flex items-center gap-2" data-testid="receiving-address">
                  <code className="text-sm break-all flex-1 text-slate-800">{data.receiving_address}</code>
                  <button onClick={() => copy(data.receiving_address)} className="text-sky-500 hover:text-sky-600 p-2 border border-slate-200 hover:border-sky-400 rounded transition-colors flex-shrink-0" data-testid="copy-address-btn" aria-label="Copy address"><Copy size={16} /></button>
                </div>
                {copied && <div className="text-sky-500 text-xs mt-1" data-testid="copied-msg">✓ Copied to clipboard</div>}
              </div>
              <div>
                <div className="overline mb-2">Time remaining</div>
                <div className={`flex items-center gap-2 font-serif-display text-3xl ${expired ? "text-red-500" : "text-slate-900"}`}>
                  <Clock size={20} /> {expired ? "Expired" : `${mm}:${ss}`}
                </div>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-md p-4 text-sm text-amber-900">
                <strong>⚠️ Send only USDT on TRC20/TRON.</strong> Funds sent on other networks (ERC20, BEP20, Solana) will be lost.
              </div>
            </div>
          </div>
        )}

        <Link to="/" className="btn-ghost mt-12 inline-flex">Back to home</Link>
      </div>
    </main>
  );
}
